import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAdmin } from '../contexts/AdminContext';
import { usePocketBase } from '../contexts/PocketBaseContext';
import RichTextEditor from './RichTextEditor';

const { FiX, FiUpload, FiTag, FiSave, FiPlus, FiTrash2, FiEye, FiEyeOff, FiEdit, FiBook, FiSearch, FiGrid, FiList, FiCheck, FiChevronDown } = FiIcons;

const AdminPanel = () => {
  const { showAdminPanel, closeAdminPanel } = useAdmin();
  const { pb, user, apiCall, setLoading: setGlobalLoading, isAdmin } = usePocketBase();
  const [currentView, setCurrentView] = useState('upload'); // 'upload', 'list', 'edit'
  const [editingBook, setEditingBook] = useState(null);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [booksLoading, setBooksLoading] = useState(false);
  const [totalBooks, setTotalBooks] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [booksPerPage] = useState(12);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [selectedTagsForAdd, setSelectedTagsForAdd] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    summary: '',
    language: 'English',
    pages: '',
    cover_source_url: '',
    audiobook_url: '',
    public: false,
    tags: []
  });
  const [files, setFiles] = useState({
    transcript_file: null,
    cover_file: null,
    audiobook_file: null
  });
  const [availableTags, setAvailableTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [multiTagInput, setMultiTagInput] = useState('');
  const [isCreatingTags, setIsCreatingTags] = useState(false);

  useEffect(() => {
    if (showAdminPanel) {
      fetchTags();
      if (user && isAdmin) {
        fetchBooks();
      }
    }
  }, [showAdminPanel, user, isAdmin, currentPage, searchQuery]);

  // Listen for edit book events
  useEffect(() => {
    const editBookEventHandler = (event) => {
      const book = event.detail;
      handleEditBook(book);
    };

    window.addEventListener('editBook', editBookEventHandler);
    return () => window.removeEventListener('editBook', editBookEventHandler);
  }, []);

  const fetchBooks = async () => {
    if (!user || !isAdmin) return;
    try {
      setBooksLoading(true);
      let filter = `owner="${user.id}"`;
      
      if (searchQuery) {
        filter += ` && (title ~ "${searchQuery}" || author ~ "${searchQuery}" || summary ~ "${searchQuery}")`;
      }

      const response = await apiCall('GET', `/api/collections/books/records?filter=${encodeURIComponent(filter)}&expand=tags&sort=-created&page=${currentPage}&perPage=${booksPerPage}`);
      setBooks(response.items || []);
      setTotalPages(response.totalPages || 1);
      setTotalBooks(response.totalItems || 0);
    } catch (error) {
      console.error('Error fetching books:', error);
      toast.error(error.message || 'Failed to fetch books');
    } finally {
      setBooksLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await apiCall('GET', '/api/collections/tags/records?sort=name');
      setAvailableTags(response.items || []);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      author: '',
      summary: '',
      language: 'English',
      pages: '',
      cover_source_url: '',
      audiobook_url: '',
      public: false,
      tags: []
    });
    setFiles({
      transcript_file: null,
      cover_file: null,
      audiobook_file: null
    });
    setEditingBook(null);
    setSelectedTagsForAdd([]);
    setNewTag('');
    setMultiTagInput('');
  };

  const checkAdminPermission = () => {
    if (!isAdmin) {
      toast.error('Only the administrator can perform this action.');
      return false;
    }
    return true;
  };

  const handleEditBook = (book) => {
    if (!checkAdminPermission()) return;
    setEditingBook(book);
    setFormData({
      title: book.title || '',
      author: book.author || '',
      summary: book.summary || '',
      language: book.language || 'English',
      pages: book.pages || '',
      cover_source_url: book.cover_source_url || '',
      audiobook_url: book.audiobook_url || '',
      public: book.public || false,
      tags: book.expand?.tags?.map(tag => tag.id) || []
    });
    setFiles({
      transcript_file: null,
      cover_file: null,
      audiobook_file: null
    });
    setSelectedTagsForAdd([]);
    setNewTag('');
    setMultiTagInput('');
    setCurrentView('edit');
  };

  const handleDeleteBook = async (book) => {
    if (!checkAdminPermission()) return;
    if (!window.confirm(`Are you sure you want to delete "${book.title}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await apiCall('DELETE', `/api/collections/books/records/${book.id}`);
      toast.success('Book deleted successfully!');
      await fetchBooks();
      if (editingBook?.id === book.id) {
        resetForm();
        setCurrentView('upload');
      }
    } catch (error) {
      console.error('Error deleting book:', error);
      toast.error(error.message || 'Failed to delete book');
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const { name, files: selectedFiles } = e.target;
    setFiles(prev => ({
      ...prev,
      [name]: selectedFiles[0] || null
    }));
  };

  const handleTagToggle = (tagId) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tagId)
        ? prev.tags.filter(id => id !== tagId)
        : [...prev.tags, tagId]
    }));
  };

  const handleMultiTagAdd = () => {
    if (selectedTagsForAdd.length === 0) {
      toast.error('Please select at least one tag to add');
      return;
    }
    
    const newTags = [...new Set([...formData.tags, ...selectedTagsForAdd])];
    setFormData(prev => ({
      ...prev,
      tags: newTags
    }));
    setSelectedTagsForAdd([]);
    setShowTagDropdown(false);
    toast.success(`${selectedTagsForAdd.length} tag(s) added successfully!`);
  };

  const handleTagForAddToggle = (tagId) => {
    setSelectedTagsForAdd(prev => 
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const createNewTag = async () => {
    if (!checkAdminPermission()) return;
    if (!newTag.trim()) return;
    try {
      const response = await apiCall('POST', '/api/collections/tags/records', {
        name: newTag.trim()
      });
      setAvailableTags(prev => [...prev, response]);
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, response.id]
      }));
      setNewTag('');
      toast.success('Tag created successfully!');
    } catch (error) {
      console.error('Error creating tag:', error);
      toast.error(error.message || 'Failed to create tag');
    }
  };

  const createMultipleTags = async () => {
    if (!checkAdminPermission()) return;
    if (!multiTagInput.trim()) {
      toast.error('Please enter at least one tag name');
      return;
    }

    setIsCreatingTags(true);
    try {
      // Split by comma and clean up each tag name
      const tagNames = multiTagInput
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      if (tagNames.length === 0) {
        toast.error('Please enter valid tag names');
        return;
      }

      // Check for existing tags to avoid duplicates
      const existingTagNames = availableTags.map(tag => tag.name.toLowerCase());
      const newTagNames = tagNames.filter(name => 
        !existingTagNames.includes(name.toLowerCase())
      );

      if (newTagNames.length === 0) {
        toast.error('All entered tags already exist');
        return;
      }

      // Create slug from tag name (lowercase, replace spaces with hyphens, remove special chars)
      const createSlug = (name) => {
        return name
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .trim();
      };

      // Prepare batch requests using the exact format from the curl example
      const requests = newTagNames.map(tagName => ({
        "method": "POST",
        "url": "/api/collections/tags/records",
        "body": {
          "name": tagName,
          "slug": createSlug(tagName)
        }
      }));

      // Use the batch API endpoint with exact format
      const batchResponse = await apiCall('POST', '/api/batch', {
        "requests": requests
      });

      // Extract created tags from batch response
      // The response format should match the curl example structure
      const createdTags = batchResponse.results || batchResponse || [];

      // Update available tags with newly created tags
      setAvailableTags(prev => [...prev, ...createdTags]);

      // Auto-select newly created tags
      const newTagIds = createdTags.map(tag => tag.id || tag);
      setFormData(prev => ({
        ...prev,
        tags: [...new Set([...prev.tags, ...newTagIds])]
      }));

      // Clear the multi-tag input
      setMultiTagInput('');

      // Show success message
      const duplicateCount = tagNames.length - newTagNames.length;
      if (duplicateCount > 0) {
        toast.success(`Created ${newTagNames.length} new tag(s). ${duplicateCount} tag(s) already existed.`);
      } else {
        toast.success(`Successfully created ${newTagNames.length} new tag(s)!`);
      }

      // Refresh tags list to ensure we have the latest data
      await fetchTags();

    } catch (error) {
      console.error('Error creating multiple tags:', error);
      toast.error(error.message || 'Failed to create some tags. Please try again.');
    } finally {
      setIsCreatingTags(false);
    }
  };

  const extractPdfText = (file) => {
    return new Promise((resolve) => {
      const mockText = `This is a preview of the PDF content from "${file.name}". In a real implementation, this would contain the actual extracted text from the PDF file. The text would be processed and the first 2-5k characters would be stored as a preview.`;
      resolve(mockText);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!checkAdminPermission()) return;
    if (!user) {
      toast.error('You must be logged in to manage books');
      return;
    }
    setLoading(true);
    setGlobalLoading(true);
    try {
      const submitData = {
        ...formData,
        owner: user.id
      };

      // Handle pages conversion
      if (submitData.pages) {
        submitData.pages = parseInt(submitData.pages);
      }

      // Extract PDF text if new PDF file is provided
      if (files.transcript_file) {
        try {
          const extractedText = await extractPdfText(files.transcript_file);
          const preview = extractedText.substring(0, 2500);
          const wordCount = extractedText.split(/\s+/).length;
          submitData.transcript_preview = preview;
          submitData.words_count = wordCount;
          submitData.filesize_bytes = files.transcript_file.size;
        } catch (error) {
          console.error('Error extracting PDF text:', error);
        }
      }

      let record;
      if (editingBook) {
        // Update existing book
        record = await apiCall('PATCH', `/api/collections/books/records/${editingBook.id}`, submitData);
        toast.success('Book updated successfully!');
      } else {
        // Create new book
        record = await apiCall('POST', '/api/collections/books/records', submitData);
        toast.success('Book uploaded successfully!');
      }

      // Reset form and refresh books list
      resetForm();
      await fetchBooks();
      if (currentView === 'edit') {
        setCurrentView('upload');
      }

      // Reset file inputs
      const fileInputs = document.querySelectorAll('input[type="file"]');
      fileInputs.forEach(input => input.value = '');
    } catch (error) {
      console.error('Error saving book:', error);
      toast.error(error.message || `Failed to ${editingBook ? 'update' : 'upload'} book. Please try again.`);
    } finally {
      setLoading(false);
      setGlobalLoading(false);
    }
  };

  if (!user || !isAdmin) return null;

  return (
    <AnimatePresence>
      {showAdminPanel && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={closeAdminPanel}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <h2 className="text-2xl font-bold">
                    {currentView === 'list' ? 'Manage Books' : currentView === 'edit' ? 'Edit Book' : 'Upload New Book'}
                  </h2>
                  {currentView === 'list' && (
                    <div className="flex items-center space-x-2 text-sm">
                      <span className="bg-white/20 px-3 py-1 rounded-full">
                        {totalBooks.toLocaleString()} total books
                      </span>
                      {searchQuery && (
                        <span className="bg-white/20 px-3 py-1 rounded-full">
                          {books.length} results
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-3">
                  {/* Upload New Book Button - Always visible except when already in upload view */}
                  {currentView !== 'upload' && (
                    <motion.button
                      onClick={() => {
                        resetForm();
                        setCurrentView('upload');
                      }}
                      className="flex items-center space-x-2 px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <SafeIcon icon={FiPlus} />
                      <span>Upload New Book</span>
                    </motion.button>
                  )}
                  <button
                    onClick={closeAdminPanel}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiX} className="text-xl" />
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(95vh-88px)]">
              {currentView === 'list' ? (
                <BooksList 
                  books={books} 
                  loading={booksLoading} 
                  onEdit={handleEditBook} 
                  onDelete={handleDeleteBook}
                  onUploadNew={() => {
                    resetForm();
                    setCurrentView('upload');
                  }}
                  totalBooks={totalBooks}
                  searchQuery={searchQuery}
                  searchInput={searchInput}
                  setSearchInput={setSearchInput}
                  onSearchSubmit={handleSearchSubmit}
                  onClearSearch={handleClearSearch}
                  viewMode={viewMode}
                  setViewMode={setViewMode}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  setCurrentPage={setCurrentPage}
                />
              ) : (
                <BookForm
                  formData={formData}
                  files={files}
                  availableTags={availableTags}
                  newTag={newTag}
                  setNewTag={setNewTag}
                  multiTagInput={multiTagInput}
                  setMultiTagInput={setMultiTagInput}
                  isCreatingTags={isCreatingTags}
                  loading={loading}
                  isEditing={!!editingBook}
                  onInputChange={handleInputChange}
                  onFileChange={handleFileChange}
                  onTagToggle={handleTagToggle}
                  onCreateTag={createNewTag}
                  onCreateMultipleTags={createMultipleTags}
                  onSubmit={handleSubmit}
                  onViewAllBooks={() => {
                    setCurrentView('list');
                    resetForm();
                  }}
                  selectedTagsForAdd={selectedTagsForAdd}
                  showTagDropdown={showTagDropdown}
                  setShowTagDropdown={setShowTagDropdown}
                  handleTagForAddToggle={handleTagForAddToggle}
                  handleMultiTagAdd={handleMultiTagAdd}
                />
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const BooksList = ({ 
  books, 
  loading, 
  onEdit, 
  onDelete, 
  onUploadNew, 
  totalBooks,
  searchQuery,
  searchInput,
  setSearchInput,
  onSearchSubmit,
  onClearSearch,
  viewMode,
  setViewMode,
  currentPage,
  totalPages,
  setCurrentPage
}) => {
  const getCoverUrl = (book) => {
    if (book.cover_thumb_file) {
      return `https://leaf-surrounded-my-book-hub.pockethost.io/api/files/${book.collectionId}/${book.id}/${book.cover_thumb_file}`;
    }
    if (book.cover_source_url) {
      return book.cover_source_url;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Search and Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Your Books ({totalBooks.toLocaleString()})
          </h3>
          <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
              title="Grid view"
            >
              <SafeIcon icon={FiGrid} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
              title="List view"
            >
              <SafeIcon icon={FiList} />
            </button>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Optimized Search Form */}
          <form onSubmit={onSearchSubmit} className="flex items-center space-x-2">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by title, author, or summary..."
                className="pl-10 pr-4 py-2 w-64 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={onClearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <SafeIcon icon={FiX} className="text-sm" />
                </button>
              )}
            </div>
            <motion.button
              type="submit"
              className="flex items-center space-x-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <SafeIcon icon={FiSearch} className="text-sm" />
              <span>Search</span>
            </motion.button>
          </form>
          
          <motion.button
            onClick={onUploadNew}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <SafeIcon icon={FiPlus} />
            <span>Upload New Book</span>
          </motion.button>
        </div>
      </div>

      {/* Search Info */}
      {searchQuery && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 flex items-center justify-between">
          <span className="text-sm text-indigo-700">
            Showing results for "{searchQuery}" ({books.length} books found)
          </span>
          <button
            onClick={onClearSearch}
            className="text-sm text-indigo-600 hover:text-indigo-700"
          >
            Clear search
          </button>
        </div>
      )}

      {/* Books Display */}
      {books.length === 0 ? (
        <div className="text-center py-12">
          <SafeIcon icon={FiBook} className="text-6xl text-gray-400 mb-4 mx-auto" />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">
            {searchQuery ? 'No books found' : 'No books yet'}
          </h3>
          <p className="text-gray-500 mb-6">
            {searchQuery ? 'Try adjusting your search terms' : 'Upload your first book to get started'}
          </p>
          <motion.button
            onClick={onUploadNew}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <SafeIcon icon={FiPlus} />
            <span>Upload Your First Book</span>
          </motion.button>
        </div>
      ) : (
        <>
          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 max-h-[60vh] overflow-y-auto pr-2">
              {books.map((book) => (
                <motion.div
                  key={book.id}
                  className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all duration-300 cursor-pointer group"
                  whileHover={{ scale: 1.02, y: -2 }}
                  onClick={() => onEdit(book)}
                  title="Click to edit this book"
                >
                  {/* Cover */}
                  <div className="w-full h-32 bg-gray-100 rounded-lg overflow-hidden mb-3">
                    {getCoverUrl(book) ? (
                      <img src={getCoverUrl(book)} alt={book.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="flex items-center justify-center h-full bg-gradient-to-br from-indigo-100 to-purple-100">
                        <SafeIcon icon={FiBook} className="text-indigo-400" />
                      </div>
                    )}
                  </div>

                  {/* Book Info */}
                  <h4 className="font-semibold text-gray-900 truncate mb-1">{book.title}</h4>
                  <p className="text-sm text-gray-600 truncate mb-2">{book.author}</p>
                  
                  {/* Metadata */}
                  <div className="flex items-center justify-between text-xs">
                    <span className={`px-2 py-1 rounded-full ${
                      book.public ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {book.public ? 'Public' : 'Private'}
                    </span>
                    <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <motion.button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(book);
                        }}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit book"
                      >
                        <SafeIcon icon={FiEdit} className="text-sm" />
                      </motion.button>
                      <motion.button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(book);
                        }}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title="Delete book"
                      >
                        <SafeIcon icon={FiTrash2} className="text-sm" />
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* List View */}
          {viewMode === 'list' && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
              {books.map((book) => (
                <motion.div
                  key={book.id}
                  className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all duration-300 cursor-pointer group"
                  whileHover={{ scale: 1.01, x: 4 }}
                  onClick={() => onEdit(book)}
                  title="Click to edit this book"
                >
                  <div className="flex items-center space-x-4">
                    {/* Cover */}
                    <div className="w-16 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      {getCoverUrl(book) ? (
                        <img src={getCoverUrl(book)} alt={book.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex items-center justify-center h-full bg-gradient-to-br from-indigo-100 to-purple-100">
                          <SafeIcon icon={FiBook} className="text-indigo-400 text-sm" />
                        </div>
                      )}
                    </div>

                    {/* Book Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 truncate">{book.title}</h4>
                      <p className="text-sm text-gray-600 mb-2">{book.author}</p>
                      <div className="flex items-center space-x-4">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          book.public ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {book.public ? 'Public' : 'Private'}
                        </span>
                        {book.pages && (
                          <span className="text-xs text-gray-500">{book.pages} pages</span>
                        )}
                        {book.language && (
                          <span className="text-xs text-gray-500">{book.language}</span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <motion.button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(book);
                        }}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        title="Edit book"
                      >
                        <SafeIcon icon={FiEdit} />
                      </motion.button>
                      <motion.button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(book);
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        title="Delete book"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center space-x-2 pt-4 border-t">
              <motion.button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={`px-3 py-2 rounded-lg transition-all ${
                  currentPage === 1 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                }`}
                whileHover={currentPage !== 1 ? { scale: 1.05 } : {}}
                whileTap={currentPage !== 1 ? { scale: 0.95 } : {}}
              >
                Previous
              </motion.button>

              <div className="flex space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }).map((_, index) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = index + 1;
                  } else if (currentPage <= 3) {
                    pageNum = index + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + index;
                  } else {
                    pageNum = currentPage - 2 + index;
                  }

                  return (
                    <motion.button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-2 rounded-lg transition-all ${
                        currentPage === pageNum 
                          ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white' 
                          : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {pageNum}
                    </motion.button>
                  );
                })}
              </div>

              <motion.button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className={`px-3 py-2 rounded-lg transition-all ${
                  currentPage === totalPages 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                }`}
                whileHover={currentPage !== totalPages ? { scale: 1.05 } : {}}
                whileTap={currentPage !== totalPages ? { scale: 0.95 } : {}}
              >
                Next
              </motion.button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const BookForm = ({ 
  formData, 
  files, 
  availableTags, 
  newTag, 
  setNewTag,
  multiTagInput,
  setMultiTagInput,
  isCreatingTags,
  loading, 
  isEditing, 
  onInputChange, 
  onFileChange, 
  onTagToggle, 
  onCreateTag, 
  onCreateMultipleTags,
  onSubmit,
  onViewAllBooks,
  selectedTagsForAdd,
  showTagDropdown,
  setShowTagDropdown,
  handleTagForAddToggle,
  handleMultiTagAdd
}) => {
  const getSelectedTagNames = () => {
    return availableTags
      .filter(tag => selectedTagsForAdd.includes(tag.id))
      .map(tag => tag.name);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6" onClick={(e) => e.stopPropagation()}>
      {/* Form Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          {isEditing ? 'Edit Book' : 'Upload New Book'}
        </h3>
        <button
          type="button"
          onClick={onViewAllBooks}
          className="text-indigo-600 hover:text-indigo-700 text-sm flex items-center space-x-1"
        >
          <SafeIcon icon={FiBook} />
          <span>View All Books</span>
        </button>
      </div>

      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Title *
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={onInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Author *
          </label>
          <input
            type="text"
            name="author"
            value={formData.author}
            onChange={onInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            required
          />
        </div>
      </div>

      {/* Rich Text Summary */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Summary
        </label>
        <div className="border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-transparent">
          <RichTextEditor
            value={formData.summary}
            onChange={(value) => onInputChange({ target: { name: 'summary', value } })}
            placeholder="Enter book summary... (rich text formatting supported)"
          />
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Use the toolbar to format text with headings, bold, italics, lists, quotes, and more.
        </p>
      </div>

      {/* File Uploads */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">File Uploads</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              PDF File {isEditing && <span className="text-xs text-gray-500">(leave empty to keep current)</span>}
            </label>
            <div className="relative">
              <input
                type="file"
                name="transcript_file"
                accept=".pdf"
                onChange={onFileChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <SafeIcon icon={FiUpload} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cover Image {isEditing && <span className="text-xs text-gray-500">(leave empty to keep current)</span>}
            </label>
            <input
              type="file"
              name="cover_file"
              accept="image/*"
              onChange={onFileChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cover Image URL (alternative to file upload)
          </label>
          <input
            type="url"
            name="cover_source_url"
            value={formData.cover_source_url}
            onChange={onInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="https://example.com/cover.jpg"
          />
        </div>
      </div>

      {/* Metadata */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Language
          </label>
          <select
            name="language"
            value={formData.language}
            onChange={onInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="English">English</option>
            <option value="Spanish">Spanish</option>
            <option value="French">French</option>
            <option value="German">German</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Pages
          </label>
          <input
            type="number"
            name="pages"
            value={formData.pages}
            onChange={onInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Audiobook URL
          </label>
          <input
            type="url"
            name="audiobook_url"
            value={formData.audiobook_url}
            onChange={onInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="https://example.com/audiobook.mp3"
          />
        </div>
      </div>

      {/* Enhanced Tags Section */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tags
        </label>
        
        {/* Multi-Tag Add Feature */}
        <div className="mb-4">
          <div className="flex items-center space-x-2 mb-2">
            <span className="text-sm text-gray-600">Quick add multiple tags:</span>
            <button
              type="button"
              onClick={() => setShowTagDropdown(!showTagDropdown)}
              className="flex items-center space-x-2 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors"
            >
              <SafeIcon icon={FiPlus} className="text-sm" />
              <span className="text-sm">Select Multiple</span>
            </button>
          </div>

          {/* Multi-Tag Dropdown */}
          {showTagDropdown && (
            <div className="relative">
              <div className="absolute z-20 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                <div className="p-3 border-b border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Select tags to add ({selectedTagsForAdd.length} selected)
                    </span>
                    {selectedTagsForAdd.length > 0 && (
                      <button
                        type="button"
                        onClick={handleMultiTagAdd}
                        className="flex items-center space-x-1 px-2 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                      >
                        <SafeIcon icon={FiCheck} className="text-xs" />
                        <span>Add Selected</span>
                      </button>
                    )}
                  </div>
                  {selectedTagsForAdd.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {getSelectedTagNames().map((name) => (
                        <span key={name} className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full">
                          {name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="p-2">
                  {availableTags.map((tag) => (
                    <label
                      key={tag.id}
                      className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedTagsForAdd.includes(tag.id)}
                        onChange={() => handleTagForAddToggle(tag.id)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700">{tag.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Multiple Tag Creation */}
        <div className="mb-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
          <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
            <SafeIcon icon={FiTag} />
            <span>Create Multiple Tags at Once</span>
          </h4>
          <div className="space-y-2">
            <textarea
              value={multiTagInput}
              onChange={(e) => setMultiTagInput(e.target.value)}
              placeholder="Enter tag names separated by commas (e.g., Fiction, Mystery, Bestseller)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
              rows={2}
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {multiTagInput.trim() ? 
                  `${multiTagInput.split(',').filter(tag => tag.trim()).length} tag(s) will be created` : 
                  'Enter tag names separated by commas'
                }
              </span>
              <motion.button
                type="button"
                onClick={onCreateMultipleTags}
                disabled={isCreatingTags || !multiTagInput.trim()}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                whileHover={!isCreatingTags && multiTagInput.trim() ? { scale: 1.05 } : {}}
                whileTap={!isCreatingTags && multiTagInput.trim() ? { scale: 0.95 } : {}}
              >
                {isCreatingTags ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiPlus} />
                    <span>Create Tags</span>
                  </>
                )}
              </motion.button>
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-600">
            💡 Tip: Duplicate tags will be automatically skipped. New tags will be automatically selected for this book.
          </p>
        </div>

        {/* Single Tag Creation */}
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            placeholder="Create single tag..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
          <button
            type="button"
            onClick={onCreateTag}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <SafeIcon icon={FiPlus} />
          </button>
        </div>

        {/* Currently selected tags */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-2">Currently selected tags:</div>
          <div className="flex flex-wrap gap-2">
            {formData.tags.length > 0 ? (
              availableTags
                .filter(tag => formData.tags.includes(tag.id))
                .map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => onTagToggle(tag.id)}
                    className="flex items-center space-x-1 px-3 py-1 bg-indigo-600 text-white rounded-full text-sm hover:bg-indigo-700 transition-colors"
                  >
                    <SafeIcon icon={FiTag} className="text-xs" />
                    <span>{tag.name}</span>
                    <SafeIcon icon={FiX} className="text-xs" />
                  </button>
                ))
            ) : (
              <span className="text-sm text-gray-500">No tags selected</span>
            )}
          </div>
        </div>

        {/* Available tags for individual selection */}
        <div className="mt-4">
          <div className="text-sm text-gray-600 mb-2">Click to add individual tags:</div>
          <div className="flex flex-wrap gap-2">
            {availableTags
              .filter(tag => !formData.tags.includes(tag.id))
              .map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => onTagToggle(tag.id)}
                  className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200 transition-colors"
                >
                  <SafeIcon icon={FiTag} className="mr-1 text-xs" />
                  {tag.name}
                </button>
              ))}
          </div>
        </div>
      </div>

      {/* Public Toggle */}
      <div className="flex items-center space-x-3">
        <input
          type="checkbox"
          id="public"
          name="public"
          checked={formData.public}
          onChange={onInputChange}
          className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
        />
        <label htmlFor="public" className="flex items-center space-x-2 text-sm font-medium text-gray-700">
          <SafeIcon icon={formData.public ? FiEye : FiEyeOff} />
          <span>Make this book public</span>
        </label>
      </div>

      {/* Submit Button */}
      <motion.button
        type="submit"
        disabled={loading}
        className="w-full flex items-center justify-center space-x-2 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:shadow-lg disabled:opacity-50 transition-all duration-300"
        whileHover={{ scale: loading ? 1 : 1.02 }}
        whileTap={{ scale: loading ? 1 : 0.98 }}
      >
        <SafeIcon icon={FiSave} />
        <span>{loading ? (isEditing ? 'Updating...' : 'Uploading...') : (isEditing ? 'Update Book' : 'Upload Book')}</span>
      </motion.button>
    </form>
  );
};

export default AdminPanel;